import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, Search, Lock, Globe, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const GroupsPage = () => {
  const { user, api } = useAuth();
  const [allGroups, setAllGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    cover_image: '',
    is_private: false,
    genre_focus: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsRes, genresRes] = await Promise.all([
          api.get('/groups'),
          api.get('/genres')
        ]);
        setAllGroups(groupsRes.data);
        setGenres(genresRes.data);

        if (user) {
          const myGroupsRes = await api.get('/groups/my');
          setMyGroups(myGroupsRes.data);
        }
      } catch (error) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, user]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim() || !newGroup.description.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/groups', newGroup);
      setAllGroups([res.data, ...allGroups]);
      setMyGroups([res.data, ...myGroups]);
      setNewGroup({ name: '', description: '', cover_image: '', is_private: false, genre_focus: '' });
      setShowNewDialog(false);
    } catch (error) {
      // Non-critical error suppressed
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await api.post(`/groups/${groupId}/join`);
      const group = allGroups.find(g => g.id === groupId);
      if (group) {
        setMyGroups([...myGroups, group]);
        setAllGroups(allGroups.map(g => 
          g.id === groupId ? { ...g, member_count: g.member_count + 1 } : g
        ));
      }
    } catch (error) {
      // Non-critical error suppressed
    }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      await api.post(`/groups/${groupId}/leave`);
      setMyGroups(myGroups.filter(g => g.id !== groupId));
      setAllGroups(allGroups.map(g => 
        g.id === groupId ? { ...g, member_count: g.member_count - 1 } : g
      ));
    } catch (error) {
      // Non-critical error suppressed
    }
  };

  const filteredGroups = allGroups.filter(g => 
    g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isInGroup = (groupId) => myGroups.some(g => g.id === groupId);

  const GroupCard = ({ group, showJoinButton = true }) => (
    <Link to={`/groups/${group.id}`} className="block h-full" data-testid={`group-card-${group.id}`}>
    <Card className="h-full bg-white border-brand-primary/10 overflow-hidden card-hover">
      {group.cover_image && (
        <div className="h-32 overflow-hidden">
          <img 
            src={group.cover_image}
            alt={group.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <CardContent className={`p-6 ${!group.cover_image ? 'pt-8' : ''}`}>
        <div className="flex items-center gap-2 mb-3">
          {group.is_private ? (
            <Lock className="w-4 h-4 text-text-muted" />
          ) : (
            <Globe className="w-4 h-4 text-text-muted" />
          )}
          {group.genre_focus && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">
              {group.genre_focus}
            </span>
          )}
        </div>

        <h3 className="font-serif text-xl font-semibold text-text-primary mb-2">
          {group.name}
        </h3>

        <p className="text-text-secondary text-sm line-clamp-2 mb-4">
          {group.description}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-brand-primary/10">
          <span className="text-sm text-text-muted flex items-center gap-1">
            <Users className="w-4 h-4" />
            {group.member_count} members
          </span>
          {showJoinButton && user && (
            isInGroup(group.id) ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleLeaveGroup(group.id); }}
              >
                Leave
              </Button>
            ) : (
              <Button 
                size="sm"
                className="btn-primary"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleJoinGroup(group.id); }}
              >
                Join
              </Button>
            )
          )}
        </div>
      </CardContent>
    </Card>
    </Link>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="groups-page">
      {/* Header */}
      <div className="relative mb-12">
        <div className="absolute inset-0 rounded-2xl overflow-hidden">
          <img 
            src="https://images.pexels.com/photos/5257760/pexels-photo-5257760.jpeg"
            alt="Writing Groups"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/90 to-brand-primary/70" />
        </div>
        <div className="relative p-8 md:p-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="text-white">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-6 h-6 text-brand-tertiary" />
                <span className="overline text-brand-tertiary">Community</span>
              </div>
              <h1 className="font-serif text-4xl font-semibold mb-4">Writing Groups</h1>
              <p className="text-white/80 max-w-lg">
                Find your tribe. Join groups focused on your favorite genres and 
                collaborate with like-minded writers.
              </p>
            </div>
            {user && (
              <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-brand-primary hover:bg-white/90 gap-2" data-testid="create-group-button">
                    <Plus className="w-4 h-4" />
                    Create Group
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create a Writing Group</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateGroup} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Group Name *</Label>
                      <Input
                        id="name"
                        placeholder="Enter group name"
                        value={newGroup.name}
                        onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                        required
                        data-testid="group-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        placeholder="What is this group about?"
                        value={newGroup.description}
                        onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                        required
                        rows={3}
                        data-testid="group-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="genre_focus">Genre Focus (optional)</Label>
                      <Select 
                        value={newGroup.genre_focus || "any"} 
                        onValueChange={(value) => setNewGroup({ ...newGroup, genre_focus: value === "any" ? "" : value })}
                      >
                        <SelectTrigger data-testid="genre-focus-select">
                          <SelectValue placeholder="Select a genre" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any Genre</SelectItem>
                          {genres.map((genre) => (
                            <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cover_image">Cover Image URL (optional)</Label>
                      <Input
                        id="cover_image"
                        placeholder="https://..."
                        value={newGroup.cover_image}
                        onChange={(e) => setNewGroup({ ...newGroup, cover_image: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="is_private" className="flex items-center gap-2 cursor-pointer">
                        <Lock className="w-4 h-4 text-text-muted" />
                        Private Group
                      </Label>
                      <Switch
                        id="is_private"
                        checked={newGroup.is_private}
                        onCheckedChange={(checked) => setNewGroup({ ...newGroup, is_private: checked })}
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="btn-accent" disabled={submitting} data-testid="submit-group">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Create Group
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
        <Input
          type="text"
          placeholder="Search groups..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12"
          data-testid="groups-search"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue={user ? "my-groups" : "all"}>
        <TabsList className="bg-surface-alt mb-8">
          {user && <TabsTrigger value="my-groups" data-testid="tab-my-groups">My Groups</TabsTrigger>}
          <TabsTrigger value="all" data-testid="tab-all-groups">Browse All</TabsTrigger>
        </TabsList>

        {user && (
          <TabsContent value="my-groups">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
              </div>
            ) : myGroups.length === 0 ? (
              <div className="text-center py-20">
                <Users className="w-16 h-16 text-text-muted mx-auto mb-4" />
                <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">
                  You haven't joined any groups yet
                </h2>
                <p className="text-text-secondary">Browse groups and find your writing community!</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myGroups.map((group) => (
                  <Link key={group.id} to={`/groups/${group.id}`} data-testid={`my-group-${group.id}`}>
                    <GroupCard group={group} showJoinButton={false} />
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        <TabsContent value="all">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">
                No groups found
              </h2>
              <p className="text-text-secondary">Try a different search or create your own group!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGroups.map((group) => (
                <div key={group.id} data-testid={`group-card-${group.id}`}>
                  <GroupCard group={group} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GroupsPage;
